import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PromoRibbon } from "@/components/PromoRibbon";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Check } from "lucide-react";
import { Logo } from "@/components/Logo";
import { getAff } from "@/lib/affiliate";

const AUTH_IMG = "/hero-cv-comparison.png";

export default function Signup() {
  const [params] = useSearchParams();
  const leadId = params.get("lead") || "";
  const refCode = params.get("ref") || "";
  const intent = params.get("intent") || "";
  const plan = params.get("plan") || "";
  const [name, setName] = useState(params.get("name") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!agreed) {
      setError("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password, language: navigator.language, lead_id: leadId || undefined, ref: refCode || undefined, aff: getAff() || undefined });
      loginWithToken(data.token, data.user);
      if (intent === "purchase") { navigate(plan ? `/billing?plan=${plan}` : "/billing"); }
      else if (leadId) { localStorage.setItem("welcome_lead", leadId); navigate("/welcome"); }
      else navigate("/profile");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    if (!agreed) {
      setError("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (leadId) localStorage.setItem("pending_lead", leadId);
    if (refCode) localStorage.setItem("pending_ref", refCode);
    if (intent === "purchase") { localStorage.setItem("pending_intent", "purchase"); if (plan) localStorage.setItem("pending_plan", plan); }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="absolute top-3 left-2 sm:top-4 sm:left-3 z-20 rounded-lg bg-white/80 backdrop-blur px-2 py-1 shadow-sm border border-zinc-200/60">
        <Logo imgClassName="h-5" />
      </div>
      <div className="hidden lg:flex items-center justify-center relative order-1 bg-zinc-50 border-r border-zinc-200 p-10">
        <img src={AUTH_IMG} alt="Standard CV versus AI-tailored CV comparison" className="max-h-full max-w-full object-contain scale-[1.15]" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 order-2">
        <div className="w-full max-w-sm">
          <PromoRibbon className="rounded-lg mb-5 overflow-hidden shadow-sm" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Let's customise your CV for every job application — 7-day free trial.</h1>

          <ul className="mt-5 space-y-2" data-testid="signup-benefits">
            {["AI-tailored CVs for any job", "Instant cover letters", "5 personalised job matches a day", "Application tracker to stay organised"].map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" strokeWidth={2.5} />{b}</li>
            ))}
          </ul>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" data-testid="signup-name-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="signup-email-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="signup-password-input" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider">Confirm password</Label>
              <Input id="confirm-password" type="password" required minLength={6} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" data-testid="signup-confirm-password-input" className="h-11" />
            </div>

            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="signup-terms-label">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="signup-terms-checkbox" className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-[#1a3060] shrink-0" />
              <span>I have read and agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-accent underline">Terms &amp; Conditions</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="text-accent underline">Privacy Policy</a>.</span>
            </label>

            {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}

            <Button type="submit" disabled={loading || !agreed} data-testid="signup-submit-button" className="w-full h-11 active:scale-[0.98] transition-transform">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start free trial"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">Free for 7 days · no card required · cancel anytime</p>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-signup-button" className="w-full h-11 active:scale-[0.98] transition-transform">
            Continue with Google
          </Button>

          <p className="text-sm text-muted-foreground mt-8 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-accent font-medium hover:underline" data-testid="goto-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
